/**
 * Role-based access control (RBAC) types and utilities
 *
 * `UserRole` values are persisted in Firestore on `UserProfile.role`.
 * Legacy `manager` is normalized to `product` when profiles are loaded.
 *
 * Service domains use READ → WRITE → MANAGE (each level implies the ones below).
 * Assign only the *highest* level per domain in `rolePermissions`.
 */

/** Team / access bundle (string values are persisted in Firestore) */
export enum UserRole {
  VIEWER = "viewer",
  SALES = "sales",
  CUSTOMER_SUPPORT = "customer_support",
  MARKETING = "marketing",
  PRODUCT = "product",
  DEVELOPER = "developer",
  ADMIN = "admin",
}

/** Stable order for admin UI and permission matrix (least → most capability). */
export const ALL_USER_ROLES: readonly UserRole[] = [
  UserRole.VIEWER,
  UserRole.SALES,
  UserRole.CUSTOMER_SUPPORT,
  UserRole.MARKETING,
  UserRole.PRODUCT,
  UserRole.DEVELOPER,
  UserRole.ADMIN,
] as const;

const USER_ROLE_VALUES = new Set<string>(Object.values(UserRole));

/**
 * Map stored Firestore `role` strings to `UserRole`.
 * Legacy `manager` → product (closest prior “power user” tier).
 */
export function normalizeUserRole(raw: string | undefined | null): UserRole {
  if (raw === "manager") {
    return UserRole.PRODUCT;
  }
  if (raw && USER_ROLE_VALUES.has(raw)) {
    return raw as UserRole;
  }
  return UserRole.VIEWER;
}

/** CMS / dashboard modules with read → write → manage levels. */
export enum ServiceDomain {
  AppVersion = "app_version",
  MobileTracking = "mobile_tracking",
  DynamicLinks = "dynamic_links",
  AppFaq = "app_faq",
  FeatureFlags = "feature_flags",
  HappeningNow = "happening_now",
  Broadcasts = "broadcasts",
  ImageHosting = "image_hosting",
  PortalNotifications = "portal_notifications",
}

export type ServiceAccessLevel = "read" | "write" | "manage";

export enum Permission {
  VIEW_DASHBOARD = "view_dashboard",
  VIEW_ANALYTICS = "view_analytics",
  VIEW_ACTIVITY = "view_activity",
  EXPORT_ACTIVITY = "export_activity",
  VIEW_APP_VERSION = "view_app_version",
  MANAGE_APP_VERSION = "manage_app_version",
  VIEW_MOBILE_TRACKING = "view_mobile_tracking",

  READ_DYNAMIC_LINKS = "dynamic_links:read",
  WRITE_DYNAMIC_LINKS = "dynamic_links:write",
  MANAGE_DYNAMIC_LINKS = "dynamic_links:manage",

  READ_APP_FAQ = "app_faq:read",
  WRITE_APP_FAQ = "app_faq:write",
  MANAGE_APP_FAQ = "app_faq:manage",

  READ_FEATURE_FLAGS = "feature_flags:read",
  WRITE_FEATURE_FLAGS = "feature_flags:write",
  MANAGE_FEATURE_FLAGS = "feature_flags:manage",

  READ_HAPPENING_NOW = "happening_now:read",
  WRITE_HAPPENING_NOW = "happening_now:write",
  MANAGE_HAPPENING_NOW = "happening_now:manage",

  READ_BROADCASTS = "broadcasts:read",
  WRITE_BROADCASTS = "broadcasts:write",
  MANAGE_BROADCASTS = "broadcasts:manage",

  READ_IMAGE_HOSTING = "image_hosting:read",
  WRITE_IMAGE_HOSTING = "image_hosting:write",
  MANAGE_IMAGE_HOSTING = "image_hosting:manage",

  READ_PORTAL_NOTIFICATIONS = "portal_notifications:read",
  WRITE_PORTAL_NOTIFICATIONS = "portal_notifications:write",
  MANAGE_PORTAL_NOTIFICATIONS = "portal_notifications:manage",

  EXPORT_DATA = "export_data",
  MANAGE_USERS = "manage_users",
  CHANGE_SETTINGS = "change_settings",
  DELETE_STALE_ENTRIES = "delete_stale_entries",
  /** View & triage user-submitted bug reports / feature requests. */
  VIEW_FEEDBACK = "view_feedback",
}

const SERVICE_PERMISSIONS: Record<
  ServiceDomain,
  Record<ServiceAccessLevel, Permission>
> = {
  [ServiceDomain.AppVersion]: {
    read: Permission.VIEW_APP_VERSION,
    write: Permission.VIEW_APP_VERSION,
    manage: Permission.VIEW_APP_VERSION,
  },
  [ServiceDomain.MobileTracking]: {
    read: Permission.VIEW_MOBILE_TRACKING,
    write: Permission.VIEW_MOBILE_TRACKING,
    manage: Permission.VIEW_MOBILE_TRACKING,
  },
  [ServiceDomain.DynamicLinks]: {
    read: Permission.READ_DYNAMIC_LINKS,
    write: Permission.WRITE_DYNAMIC_LINKS,
    manage: Permission.MANAGE_DYNAMIC_LINKS,
  },
  [ServiceDomain.AppFaq]: {
    read: Permission.READ_APP_FAQ,
    write: Permission.WRITE_APP_FAQ,
    manage: Permission.MANAGE_APP_FAQ,
  },
  [ServiceDomain.FeatureFlags]: {
    read: Permission.READ_FEATURE_FLAGS,
    write: Permission.WRITE_FEATURE_FLAGS,
    manage: Permission.MANAGE_FEATURE_FLAGS,
  },
  [ServiceDomain.HappeningNow]: {
    read: Permission.READ_HAPPENING_NOW,
    write: Permission.WRITE_HAPPENING_NOW,
    manage: Permission.MANAGE_HAPPENING_NOW,
  },
  [ServiceDomain.Broadcasts]: {
    read: Permission.READ_BROADCASTS,
    write: Permission.WRITE_BROADCASTS,
    manage: Permission.MANAGE_BROADCASTS,
  },
  [ServiceDomain.ImageHosting]: {
    read: Permission.READ_IMAGE_HOSTING,
    write: Permission.WRITE_IMAGE_HOSTING,
    manage: Permission.MANAGE_IMAGE_HOSTING,
  },
  [ServiceDomain.PortalNotifications]: {
    read: Permission.READ_PORTAL_NOTIFICATIONS,
    write: Permission.WRITE_PORTAL_NOTIFICATIONS,
    manage: Permission.MANAGE_PORTAL_NOTIFICATIONS,
  },
};

const LEVEL_RANK: Record<ServiceAccessLevel, number> = {
  read: 1,
  write: 2,
  manage: 3,
};

export interface UserProfile {
  email: string;
  role: UserRole;
  displayName?: string;
  department?: string;
  createdAt: number;
  lastLogin: number;
  isActive: boolean;
}

const rolePermissions: Record<UserRole, Permission[]> = {
  [UserRole.VIEWER]: [Permission.VIEW_DASHBOARD],

  [UserRole.SALES]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_ANALYTICS,
    Permission.VIEW_APP_VERSION,
    Permission.READ_DYNAMIC_LINKS,
    Permission.WRITE_DYNAMIC_LINKS,
    Permission.READ_APP_FAQ,
    Permission.READ_HAPPENING_NOW,
  ],

  [UserRole.CUSTOMER_SUPPORT]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_ANALYTICS,
    Permission.VIEW_APP_VERSION,
    Permission.READ_DYNAMIC_LINKS,
    Permission.WRITE_DYNAMIC_LINKS,
    Permission.READ_APP_FAQ,
    Permission.READ_HAPPENING_NOW,
    Permission.READ_BROADCASTS,
    Permission.READ_IMAGE_HOSTING,
    Permission.WRITE_IMAGE_HOSTING,
    Permission.READ_PORTAL_NOTIFICATIONS,
  ],

  [UserRole.MARKETING]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_ANALYTICS,
    Permission.VIEW_APP_VERSION,
    Permission.VIEW_MOBILE_TRACKING,
    Permission.READ_DYNAMIC_LINKS,
    Permission.WRITE_DYNAMIC_LINKS,
    Permission.READ_APP_FAQ,
    Permission.WRITE_APP_FAQ,
    Permission.READ_FEATURE_FLAGS,
    Permission.READ_HAPPENING_NOW,
    Permission.WRITE_HAPPENING_NOW,
    Permission.MANAGE_HAPPENING_NOW,
    Permission.READ_BROADCASTS,
    Permission.WRITE_BROADCASTS,
    Permission.MANAGE_BROADCASTS,
    Permission.READ_IMAGE_HOSTING,
    Permission.WRITE_IMAGE_HOSTING,
    Permission.READ_PORTAL_NOTIFICATIONS,
    Permission.WRITE_PORTAL_NOTIFICATIONS,
    Permission.MANAGE_PORTAL_NOTIFICATIONS,
    Permission.EXPORT_DATA,
  ],

  [UserRole.PRODUCT]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_ANALYTICS,
    Permission.VIEW_ACTIVITY,
    Permission.VIEW_APP_VERSION,
    Permission.VIEW_MOBILE_TRACKING,
    Permission.READ_DYNAMIC_LINKS,
    Permission.WRITE_DYNAMIC_LINKS,
    Permission.MANAGE_DYNAMIC_LINKS,
    Permission.READ_APP_FAQ,
    Permission.WRITE_APP_FAQ,
    Permission.READ_FEATURE_FLAGS,
    Permission.MANAGE_FEATURE_FLAGS,
    Permission.READ_HAPPENING_NOW,
    Permission.WRITE_HAPPENING_NOW,
    Permission.READ_BROADCASTS,
    Permission.WRITE_BROADCASTS,
    Permission.READ_IMAGE_HOSTING,
    Permission.WRITE_IMAGE_HOSTING,
    Permission.READ_PORTAL_NOTIFICATIONS,
    Permission.WRITE_PORTAL_NOTIFICATIONS,
    Permission.EXPORT_DATA,
    Permission.CHANGE_SETTINGS,
  ],

  [UserRole.DEVELOPER]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_ANALYTICS,
    Permission.VIEW_ACTIVITY,
    Permission.VIEW_APP_VERSION,
    Permission.VIEW_MOBILE_TRACKING,
    Permission.MANAGE_APP_VERSION,
    Permission.READ_DYNAMIC_LINKS,
    Permission.WRITE_DYNAMIC_LINKS,
    Permission.MANAGE_DYNAMIC_LINKS,
    Permission.READ_APP_FAQ,
    Permission.WRITE_APP_FAQ,
    Permission.MANAGE_APP_FAQ,
    Permission.READ_FEATURE_FLAGS,
    Permission.WRITE_FEATURE_FLAGS,
    Permission.MANAGE_FEATURE_FLAGS,
    Permission.READ_HAPPENING_NOW,
    Permission.WRITE_HAPPENING_NOW,
    Permission.MANAGE_HAPPENING_NOW,
    Permission.READ_BROADCASTS,
    Permission.WRITE_BROADCASTS,
    Permission.MANAGE_BROADCASTS,
    Permission.READ_IMAGE_HOSTING,
    Permission.WRITE_IMAGE_HOSTING,
    Permission.MANAGE_IMAGE_HOSTING,
    Permission.READ_PORTAL_NOTIFICATIONS,
    Permission.WRITE_PORTAL_NOTIFICATIONS,
    Permission.MANAGE_PORTAL_NOTIFICATIONS,
    Permission.CHANGE_SETTINGS,
    Permission.DELETE_STALE_ENTRIES,
  ],

  [UserRole.ADMIN]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_ANALYTICS,
    Permission.VIEW_ACTIVITY,
    Permission.VIEW_APP_VERSION,
    Permission.VIEW_MOBILE_TRACKING,
    Permission.EXPORT_ACTIVITY,
    Permission.MANAGE_APP_VERSION,
    Permission.READ_DYNAMIC_LINKS,
    Permission.WRITE_DYNAMIC_LINKS,
    Permission.MANAGE_DYNAMIC_LINKS,
    Permission.READ_APP_FAQ,
    Permission.WRITE_APP_FAQ,
    Permission.MANAGE_APP_FAQ,
    Permission.READ_FEATURE_FLAGS,
    Permission.WRITE_FEATURE_FLAGS,
    Permission.MANAGE_FEATURE_FLAGS,
    Permission.READ_HAPPENING_NOW,
    Permission.WRITE_HAPPENING_NOW,
    Permission.MANAGE_HAPPENING_NOW,
    Permission.READ_BROADCASTS,
    Permission.WRITE_BROADCASTS,
    Permission.MANAGE_BROADCASTS,
    Permission.READ_IMAGE_HOSTING,
    Permission.WRITE_IMAGE_HOSTING,
    Permission.MANAGE_IMAGE_HOSTING,
    Permission.READ_PORTAL_NOTIFICATIONS,
    Permission.WRITE_PORTAL_NOTIFICATIONS,
    Permission.MANAGE_PORTAL_NOTIFICATIONS,
    Permission.EXPORT_DATA,
    Permission.MANAGE_USERS,
    Permission.CHANGE_SETTINGS,
    Permission.DELETE_STALE_ENTRIES,
    Permission.VIEW_FEEDBACK,
  ],
};

export function hasPermission(role: UserRole, permission: Permission): boolean {
  return rolePermissions[role]?.includes(permission) ?? false;
}

export function hasAnyPermission(
  role: UserRole,
  permissions: Permission[],
): boolean {
  return permissions.some((permission) => hasPermission(role, permission));
}

export function hasAllPermissions(
  role: UserRole,
  permissions: Permission[],
): boolean {
  return permissions.every((permission) => hasPermission(role, permission));
}

export function getPermissionsForRole(role: UserRole): Permission[] {
  return rolePermissions[role] ?? [];
}

/** Highest explicit level granted for a service domain (manage ⊃ write ⊃ read). */
export function serviceAccessLevel(
  role: UserRole,
  domain: ServiceDomain,
): ServiceAccessLevel | null {
  const perms = SERVICE_PERMISSIONS[domain];
  if (hasPermission(role, perms.manage)) return "manage";
  if (hasPermission(role, perms.write)) return "write";
  if (hasPermission(role, perms.read)) return "read";
  return null;
}

export function canReadService(role: UserRole, domain: ServiceDomain): boolean {
  return serviceAccessLevel(role, domain) != null;
}

export function canWriteService(
  role: UserRole,
  domain: ServiceDomain,
): boolean {
  const level = serviceAccessLevel(role, domain);
  return level === "write" || level === "manage";
}

export function canManageService(
  role: UserRole,
  domain: ServiceDomain,
): boolean {
  return serviceAccessLevel(role, domain) === "manage";
}

export function hasServiceAccessAtLeast(
  role: UserRole,
  domain: ServiceDomain,
  minimum: ServiceAccessLevel,
): boolean {
  const level = serviceAccessLevel(role, domain);
  if (!level) return false;
  return LEVEL_RANK[level] >= LEVEL_RANK[minimum];
}

/** Roles with at least `minimum` access on a service domain (stable ALL_USER_ROLES order). */
export function rolesWithServiceAccessAtLeast(
  domain: ServiceDomain,
  minimum: ServiceAccessLevel,
): UserRole[] {
  return ALL_USER_ROLES.filter((role) =>
    hasServiceAccessAtLeast(role, domain, minimum),
  );
}

/** Roles explicitly granted a permission in `rolePermissions`. */
export function rolesWithPermission(permission: Permission): UserRole[] {
  return ALL_USER_ROLES.filter((role) => hasPermission(role, permission));
}

export function getPermissionDisplayName(permission: Permission): string {
  const labels: Record<Permission, string> = {
    [Permission.VIEW_DASHBOARD]: "Dashboard",
    [Permission.VIEW_ANALYTICS]: "Analytics",
    [Permission.VIEW_ACTIVITY]: "Activity",
    [Permission.EXPORT_ACTIVITY]: "Export activity log",
    [Permission.VIEW_APP_VERSION]: "App release versions",
    [Permission.MANAGE_APP_VERSION]: "Manage app version policy",
    [Permission.VIEW_MOBILE_TRACKING]: "Mobile tracking",
    [Permission.READ_DYNAMIC_LINKS]: "Dynamic links — read",
    [Permission.WRITE_DYNAMIC_LINKS]: "Dynamic links — write",
    [Permission.MANAGE_DYNAMIC_LINKS]: "Dynamic links — manage",
    [Permission.READ_APP_FAQ]: "In-app FAQ — read",
    [Permission.WRITE_APP_FAQ]: "In-app FAQ — write",
    [Permission.MANAGE_APP_FAQ]: "In-app FAQ — manage",
    [Permission.READ_FEATURE_FLAGS]: "Feature flags — read",
    [Permission.WRITE_FEATURE_FLAGS]: "Feature flags — write",
    [Permission.MANAGE_FEATURE_FLAGS]: "Feature flags — manage",
    [Permission.READ_HAPPENING_NOW]: "Happening now — read",
    [Permission.WRITE_HAPPENING_NOW]: "Happening now — write",
    [Permission.MANAGE_HAPPENING_NOW]: "Happening now — manage",
    [Permission.READ_BROADCASTS]: "Broadcasts — read",
    [Permission.WRITE_BROADCASTS]: "Broadcasts — write",
    [Permission.MANAGE_BROADCASTS]: "Broadcasts — manage",
    [Permission.READ_IMAGE_HOSTING]: "Image hosting — read",
    [Permission.WRITE_IMAGE_HOSTING]: "Image hosting — write",
    [Permission.MANAGE_IMAGE_HOSTING]: "Image hosting — manage",
    [Permission.READ_PORTAL_NOTIFICATIONS]: "Portal notifications — read",
    [Permission.WRITE_PORTAL_NOTIFICATIONS]: "Portal notifications — write",
    [Permission.MANAGE_PORTAL_NOTIFICATIONS]: "Portal notifications — manage",
    [Permission.EXPORT_DATA]: "Export data",
    [Permission.MANAGE_USERS]: "Manage users",
    [Permission.CHANGE_SETTINGS]: "Change settings",
    [Permission.DELETE_STALE_ENTRIES]: "Delete stale entries",
    [Permission.VIEW_FEEDBACK]: "View feedback reports",
  };
  return labels[permission] ?? permission;
}

export function getServiceDomainLabel(domain: ServiceDomain): string {
  const labels: Record<ServiceDomain, string> = {
    [ServiceDomain.AppVersion]: "App release versions",
    [ServiceDomain.MobileTracking]: "Mobile tracking",
    [ServiceDomain.DynamicLinks]: "Dynamic links",
    [ServiceDomain.AppFaq]: "In-app FAQ",
    [ServiceDomain.FeatureFlags]: "Feature flags",
    [ServiceDomain.HappeningNow]: "Happening now",
    [ServiceDomain.Broadcasts]: "Broadcasts",
    [ServiceDomain.ImageHosting]: "Image hosting",
    [ServiceDomain.PortalNotifications]: "Portal notifications",
  };
  return labels[domain];
}

export function isAdmin(role: UserRole): boolean {
  return role === UserRole.ADMIN;
}

export function getRoleDisplayName(role: UserRole): string {
  const displayNames: Record<UserRole, string> = {
    [UserRole.ADMIN]: "Administrator",
    [UserRole.DEVELOPER]: "Developer",
    [UserRole.PRODUCT]: "Product",
    [UserRole.MARKETING]: "Marketing",
    [UserRole.CUSTOMER_SUPPORT]: "Customer support",
    [UserRole.SALES]: "Sales",
    [UserRole.VIEWER]: "Viewer",
  };
  return displayNames[role] ?? role;
}

/** Non-service permissions shown in the admin matrix (stable order). */
export const MATRIX_GENERAL_PERMISSIONS: readonly Permission[] = [
  Permission.VIEW_DASHBOARD,
  Permission.VIEW_ANALYTICS,
  Permission.VIEW_ACTIVITY,
  Permission.EXPORT_ACTIVITY,
  Permission.VIEW_MOBILE_TRACKING,
  Permission.EXPORT_DATA,
  Permission.MANAGE_USERS,
  Permission.CHANGE_SETTINGS,
  Permission.DELETE_STALE_ENTRIES,
  Permission.VIEW_FEEDBACK,
] as const;

/** Service domains shown in the admin matrix (one row each, R/W/M per cell). */
export const MATRIX_SERVICE_DOMAINS: readonly ServiceDomain[] = [
  ServiceDomain.AppVersion,
  ServiceDomain.DynamicLinks,
  ServiceDomain.AppFaq,
  ServiceDomain.FeatureFlags,
  ServiceDomain.HappeningNow,
  ServiceDomain.Broadcasts,
  ServiceDomain.ImageHosting,
  ServiceDomain.PortalNotifications,
] as const;

/** @deprecated Use {@link MATRIX_GENERAL_PERMISSIONS} and {@link MATRIX_SERVICE_DOMAINS}. */
export const MATRIX_PERMISSIONS: readonly Permission[] =
  MATRIX_GENERAL_PERMISSIONS;

export type ServiceMatrixLevels = {
  read: boolean;
  write: boolean;
  manage: boolean;
};

/** Effective R/W/M flags for a role on a service (manage ⊃ write ⊃ read). */
export function serviceMatrixLevels(
  role: UserRole,
  domain: ServiceDomain,
): ServiceMatrixLevels {
  const level = serviceAccessLevel(role, domain);
  if (!level) {
    return { read: false, write: false, manage: false };
  }
  return {
    read: true,
    write: LEVEL_RANK[level] >= LEVEL_RANK.write,
    manage: level === "manage",
  };
}

/** True when the role's explicit grant satisfies the cell (respects manage ⊃ write ⊃ read). */
export function roleHasMatrixPermission(
  role: UserRole,
  permission: Permission,
): boolean {
  if (hasPermission(role, permission)) return true;

  for (const domain of Object.values(ServiceDomain)) {
    const perms = SERVICE_PERMISSIONS[domain];
    const level = serviceAccessLevel(role, domain);
    if (!level) continue;
    if (permission === perms.read) return true;
    if (permission === perms.write && LEVEL_RANK[level] >= LEVEL_RANK.write) {
      return true;
    }
    if (permission === perms.manage && level === "manage") return true;
  }
  return false;
}
